import { ContentView } from '../../api/client'
import Spinner from '../Spinner'

type Props =
    // `message` replaces the drawer's wording where the surface is not one thread (the
    // post page's Threaded tab loads and fetches a whole post).
    | { kind: 'loading'; platformName: string; message?: string }
    | { kind: 'fetching'; platformName: string; message?: string }
    | { kind: 'parent-unavailable'; platformName: string; focus: ContentView | undefined }
    | { kind: 'no-replies'; platformName: string }

/**
 * The drawer's non-thread states. Each says where the data came from and never
 * sends the moderator to a deep link that may fail (the ticket's P1).
 */
export default function ConversationPlaceholder(props: Props) {
    const base = 'rounded-lg border border-dashed border-ink-300 bg-ink-50 p-4 text-sm'
    // Shown while our own getCommentThread query is pending, before we know
    // whether a platform fetch is needed. Without it the drawer body is blank
    // and reads as broken.
    if (props.kind === 'loading') {
        return (
            <div className={`${base} flex items-center gap-3 text-ink-500`}>
                <Spinner size={4} />
                <span>{props.message ?? 'Loading the conversation'}</span>
            </div>
        )
    }
    if (props.kind === 'fetching') {
        return (
            <div className={`${base} flex items-center gap-3 text-ink-500`}>
                <Spinner size={4} />
                <span>
                    {props.message ?? `Loading the full conversation from ${props.platformName}…`}
                </span>
            </div>
        )
    }
    if (props.kind === 'parent-unavailable') {
        const link = props.focus?.socialMediaContentLink
        return (
            <div className={`${base} flex flex-col gap-1`}>
                <span className="font-medium text-ink-900">Parent comment unavailable</span>
                <span className="text-ink-500">
                    {props.platformName} no longer returns it. The replies below are what Arwen
                    holds.
                </span>
                {link && (
                    <a
                        href={link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-arwen-700 underline"
                    >
                        Open on {props.platformName}
                    </a>
                )}
            </div>
        )
    }
    return (
        <div className={`${base} flex flex-col gap-1`}>
            <span className="font-medium text-ink-900">No replies yet</span>
            <span className="text-ink-500">
                Arwen checks {props.platformName} for new replies each time you open this.
            </span>
        </div>
    )
}
